import { useCallback, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

const SUPPORTED_LANGS = ["sv", "en", "no", "da", "fi"];
const DEFAULT_LANG = "sv";

export function isValidLang(lang: string | undefined): lang is string {
  return !!lang && SUPPORTED_LANGS.includes(lang);
}

export function useLanguage() {
  const { lang } = useParams<{ lang: string }>();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Fallback: parse lang from pathname when outside <Routes> (useParams returns {})
  const langFromPath = location.pathname.match(/^\/(sv|en|no|da|fi)(\/|$)/)?.[1];
  const currentLang = isValidLang(lang) ? lang : isValidLang(langFromPath) ? langFromPath : DEFAULT_LANG;

  // Sync i18n with URL param and update document lang attribute
  useEffect(() => {
    if (i18n.language !== currentLang) {
      i18n.changeLanguage(currentLang);
    }
    if (typeof document !== "undefined") {
      document.documentElement.lang = currentLang;
    }
  }, [currentLang, i18n]);

  const getLocalizedPath = useCallback(
    (path: string) => {
      // Strip any existing language prefix
      const stripped = path.replace(/^\/(sv|en|no|da|fi)(\/|$)/, "/");
      const cleanPath = stripped === "" ? "/" : stripped;
      return `/${currentLang}${cleanPath === "/" ? "" : cleanPath}`;
    },
    [currentLang]
  );

  const changeLanguage = useCallback(
    (newLang: string) => {
      if (!SUPPORTED_LANGS.includes(newLang)) return;
      // Replace current lang prefix in the path
      const pathWithoutLang = location.pathname.replace(
        /^\/(sv|en|no|da|fi)(\/|$)/,
        "/"
      );
      const cleanPath = pathWithoutLang === "" ? "/" : pathWithoutLang;
      const newPath = `/${newLang}${cleanPath === "/" ? "" : cleanPath}`;
      navigate(newPath + location.search + location.hash, { replace: true });
    },
    [location.pathname, location.search, location.hash, navigate]
  );

  return { lang: currentLang, getLocalizedPath, changeLanguage };
}
